import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AccessTime,
  Description,
  Warning,
  CheckCircle,
  Close,
  Error,
  Assignment,
  EventNote,
  Notifications,
  LogoutRounded,
  Person
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { requestEmpDashboardList } from '../../Redux/DashboardState/dashBoardActionCreator';
import { parseLocalStorageJSON, setToLocalStorage } from '../../helpers/Localstorage';
import LOCAL_KEYS from '../../helpers/Localstorage';
import { mockEmpDashboardList } from '../../mockData/empDashboardList';
import { requestAlertListAction, setToogleSideNavState } from '../../Redux/AlertsState/AlertsActionCreator';
import LogoutService from '../../Services/WebServiceManager/LogoutService';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { requestGetProfilePicture } from '../../Redux/ProfileState/ProfileActionCreator';
import { MenuIcon } from "lucide-react";

const AlertsCard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(false);
  const [visitedAlerts, setVisitedAlerts] = useState(new Set());
  const [showBirthdayMessage, setShowBirthdayMessage] = useState(false);
  const [userName, setUserName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false); // State for profile dropdown
  const [profileImage, setProfileImage] = useState("/default-cover.jpg");

  
  
  const AlertsListState = useSelector((state) => state.AlertListReducer);
  const GetProfileImageReducer = useSelector(
    (state) => state.GetProfilePictureReducer
  );
  const sideNavState = useSelector((state) => state.SideNavReducer);

  const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);

  // Memoize navigation items to prevent unnecessary re-renders
  const UserNavigation = useMemo(() => [
    { name: "Dashboard", href: "/" },
    { name: "Documents", href: "/documents" },
    { name: "Manage Timesheets", href: "/manage-timesheets" },
    {name:"Profile",href:"/profile"},
    {name:"Time Off Request",href:"/time-off"},
  ], []);

  const menu = useMemo(() => [
    { name: "Profile", path: "/profile", icon: <Person /> },
    { name: "Logout", path: "logout", icon: <LogoutRounded /> },
  ], []);

  // Memoize page title calculation
  const pageTitle = useMemo(() => {
    const matchedPage = UserNavigation.find((item) => item.href === location.pathname);
    return matchedPage ? matchedPage.name : "Unknown Page";
  }, [UserNavigation, location.pathname]);

  // Memoize alert data processing
  const processedAlerts = useMemo(() => {
    if (!AlertsListState.response) return [];
    
    const newAlerts = [];
    
    if (AlertsListState.response?.rejectedTimesheetSummary?.timesheets?.length) {
      newAlerts.push({
        type: "timesheet",
        count: AlertsListState.response.rejectedTimesheetSummary.count,
        message: `${AlertsListState.response.rejectedTimesheetSummary.count} timesheet(s) rejected`,
        icon: <Error className="text-red-500" />,
        action: () => navigate("/manage-timesheets"),
        details: AlertsListState.response.rejectedTimesheetSummary.timesheets.map(({ id, period, remarks, projectName }) => ({
          id,
          period,
          reason: remarks,
          project: projectName,
        })),
      });
    }

    if (AlertsListState.response?.pendingTimesheets?.pendingProjects?.length) {
      newAlerts.push({
        type: "pending-timesheet",
        count: AlertsListState.response.pendingTimesheets.pendingProjects.length,
        message: `${AlertsListState.response.pendingTimesheets.pendingProjects.length} project(s) with pending timesheets`,
        icon: <EventNote className="text-purple-500" />,
        action: () => navigate("/manage-timesheets"),
        details: AlertsListState.response.pendingTimesheets.pendingProjects.map(
          (project, index) => ({
            id: index,
            project: project.projectName,
            reason: project.message,
          })
        ),
      });
    }

    if (AlertsListState.response?.documentSummary?.documents?.length > 0) {
      newAlerts.push({
        type: "document",
        count: AlertsListState.response.documentSummary.count,
        message: `${AlertsListState.response.documentSummary.count} document(s) expired or expiring soon`,
        icon: <Description className="text-yellow-500" />,
        action: () => navigate("/documents"),
        details: AlertsListState.response.documentSummary.documents.map((doc) => ({
          id: doc.id,
          name: doc.name,
          expiryDate: doc.expiryDate,
          status: doc.status,
        })),
      });
    }

    return newAlerts;
  }, [AlertsListState.response, navigate]);

  // Memoize unread count calculation
  const unreadCount = useMemo(() => {
    return processedAlerts.filter((alert) => !visitedAlerts.has(alert.id)).length;
  }, [processedAlerts, visitedAlerts]);

  // Memoize birthday check
  const isBirthday = useMemo(() => {
    if (!userDetails?.birthDate) return false;
    const today = new Date();
    const birthDate = new Date(userDetails.birthDate);
    return today.getMonth() === birthDate.getMonth() && today.getDate() === birthDate.getDate();
  }, [userDetails?.birthDate]);

  // Memoize has any alerts check
  const hasAnyAlerts = useMemo(() => {
    if (!AlertsListState.response) return false;
    return (
      AlertsListState.response?.rejectedTimesheetSummary?.timesheets?.length > 0 ||
      AlertsListState.response?.pendingTimesheets?.pendingProjects?.length > 0 ||
      AlertsListState.response?.documentSummary?.documents?.length > 0
    );
  }, [AlertsListState.response]);

  // Function to fetch alerts data
  const fetchAlertsData = () => {
    if (AlertsListState.response) {
      setAlerts(processedAlerts);
    }
    dispatch(requestAlertListAction());
  };

  useEffect(() => {
    dispatch(requestAlertListAction());
  }, [dispatch]);

  useEffect(() => {
    if (AlertsListState && AlertsListState.response) {
      setAlerts(processedAlerts);
    }
  }, [processedAlerts]);

  useEffect(() => {
    setHasUnreadAlerts(unreadCount > 0);
  }, [unreadCount]);

  useEffect(() => {
    if (userDetails) {
      setUserName(userDetails.firstName || "");
      if (isBirthday) {
        setShowBirthdayMessage(true);
        setIsOpen(false);
      }
    }
  }, [userDetails, isBirthday]);

  const checkAndShowAlerts = () => {
    if (!showBirthdayMessage && hasAnyAlerts) {
      setTimeout(() => {
        // setIsOpen(true);
      }, 100);
    }
  };

  useEffect(() => {
    checkAndShowAlerts();
  }, [showBirthdayMessage, hasAnyAlerts]);

  const handleAlertClick = (alert, detail) => {
    if (detail) {
      setVisitedAlerts(
        (prev) => new Set([...prev, `${alert.type}-${detail.id}`])
      );
    } else {
      setVisitedAlerts((prev) => {
        const updatedSet = new Set([...prev]);
        alert.details.forEach((detail) =>
          updatedSet.add(`${alert.type}-${detail.id}`)
        );
        return updatedSet;
      });
    }
    if (alert.action) {
      alert.action();
    }
  };

  const handleThankYouClick = () => {
    setShowBirthdayMessage(false);
    fetchAlertsData();
    setTimeout(() => {
      setIsOpen(true);
    }, 100);
  };

  const handleBellIconClick = () => {
    fetchAlertsData();
    if (AlertsListState.response) {
      setAlerts(processedAlerts);
    }
    setIsOpen(true);
  };

  // Handle profile and logout actions
  const handleProfileClick = () => {
    navigate("/profile");
    setDropdownOpen(false);
  };
  
  useEffect(() => {
    dispatch(requestGetProfilePicture());
  }, [dispatch]);

  const handleProfileIconClick = () => {
    navigate('/profile');
    setDropdownOpen(false);
  };

  useEffect(()=>{
    dispatch(requestGetProfilePicture())
  },[dispatch])
    
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Are you sure you want to Logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout!",
      allowOutsideClick: false,
    });

    if (result.isConfirmed) {
      try {
        await LogoutService.shared.logout();
        localStorage.removeItem(LOCAL_KEYS.USER_DETAILS);
        navigate("/login");
        setDropdownOpen(false);
        window.location.reload();
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "Failed to log out. Please try again.",
          icon: "error",
        });
      }
    }
  };
 
 

  return (
    <>
      <div className="flex items-center justify-between px-4 py-2">
        <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
          <button 
            className={`md:hidden ${sideNavState.isOpen ? "hidden" : ""}`}
            onClick={()=>dispatch(setToogleSideNavState())}
          >
            <MenuIcon size={32}/>
          </button>
          <span>{pageTitle}</span>
        </h3>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center space-x-4"
          >
            <button
              onClick={handleBellIconClick}
              className="relative p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <Notifications className="text-gray-600" />
              {hasUnreadAlerts && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <div className="relative">
              <div
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="h-11 w-11 flex focus:ring-4 focus:ring-gray-50 bg-primary text-white
                  dark:focus:ring-gray-600 cursor-pointer  
                  hover:shadow-md hover:shadow-gray-400 md:me-0 hover:transition-all hover:border-gray hover:border-4
                  rounded-full shadow-md justify-center items-center"
              >
                <img
                  src={GetProfileImageReducer.response || profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <AnimatePresence>
                
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={handleProfileClick}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                      >
                        <Person className='mr-2 text-indigo-500' />
                        Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                      >
                        <LogoutRounded className="mr-2 text-red-500" />
                        Logout
                      </button>
                    </div>
                    
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 w-full max-w-sm mx-auto sm:max-w-md md:max-w-lg lg:max-w-xl"
          >
            <div className="bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Alerts</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Close />
                </button>
              </div>

              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {alerts.length > 0 ? (
                  alerts.map((alert, index) => {
                    const unreadDetails = alert.details.filter(
                      (detail) => !visitedAlerts.has(`${alert.type}-${detail.id}`)
                    );

                    if (unreadDetails.length === 0) return null;

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex flex-col p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => handleAlertClick(alert, null)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-white rounded-full shadow-sm">
                              {alert.icon}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {alert.message}
                              </p>
                              <p className="text-xs text-gray-500">
                                Click to take action
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                              {unreadDetails.length}
                            </span>
                            <CheckCircle className="text-green-500" />
                          </div>
                        </div>
                        <div className="mt-2 pl-11 space-y-1">
                          {unreadDetails.map((detail, idx) => (
                            <div
                              key={idx}
                              className="text-xs text-gray-600 hover:bg-gray-200 p-1 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAlertClick(alert, detail);
                              }}
                            >
                              {detail.type && (
                                <span className="font-medium">{detail.type}: </span>
                              )}
                              {detail.period && <span>{detail.period}</span>}
                              {detail.project && <span> - {detail.project}</span>}
                              {detail.reason && <span> - {detail.reason}</span>}
                              {detail.name && <span>{detail.name}</span>}
                              {detail.expiryDate && (
                                <span> (Expired: {detail.expiryDate})</span>
                              )}
                              {detail.details && <span> - {detail.details}</span>}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    {AlertsListState.loading ? "Loading alerts..." : "No new alerts"}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AlertsCard;
